import api from '../api';
import type { Tire } from '@/types/tire';

export interface TireFilters {
  search?: string;
  status?: string;
  brand?: number;
  page?: number;
  per_page?: number;
}

export interface TiresResponse {
  success: boolean;
  data: Tire[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    pending_count: number;
    approved_count: number;
    rejected_count: number;
  };
}

export interface TireResponse {
  success: boolean;
  message?: string;
  data?: Tire;
}

export interface AdminTireUpdateData {
  pattern: string;
  tire_size: string;
  dot: string;
  tire_position: string;
  shop: string;
  code: string;
  invoice_number: string;
}

export interface TireStatsResponse {
  success: boolean;
  data: {
    total_tires: number;
  };
}

export interface TireStatusUpdateData {
  status?: 'approved' | 'rejected';
  rejection_reason?: string;
}

export interface BrandStats {
  brand_id: number;
  brand_name: string;
  total_customers: number;
  pending_tires: number;
  approved_tires: number;
  pending_claims: number;
}

export interface DashboardStatsResponse {
  success: boolean;
  data: BrandStats[];
}

const API_BASE = '/admin/tires';

export const adminTireService = {
  /**
   * Get all tires with filters and pagination
   */
  async getTires(filters: TireFilters): Promise<TiresResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand.toString());

    // Ensure page and per_page are valid numbers
    const page = filters.page && filters.page > 0 ? filters.page : 1;
    const perPage = filters.per_page && filters.per_page > 0 ? filters.per_page : 10;

    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    const response = await api.get(`${API_BASE}?${params.toString()}`);
    return response.data;
  },

  /**
   * Approve a tire registration
   */
  async approveTire(tireId: number): Promise<TireResponse> {
    const response = await api.post(`${API_BASE}/${tireId}/approve`);
    return response.data;
  },

  /**
   * Reject a tire registration
   */
  async rejectTire(
    tireId: number,
    data: TireStatusUpdateData
  ): Promise<TireResponse> {
    const response = await api.post(`${API_BASE}/${tireId}/reject`, data);
    return response.data;
  },

  /**
   * Update a pending tire registration (admin only)
   */
  async updateTire(
    tireId: number,
    data: AdminTireUpdateData,
    newTirePhotos?: File[],
    deletedTirePhotoIds?: number[],
    newReceiptPhotos?: File[],
    deletedReceiptPhotoIds?: number[]
  ): Promise<TireResponse> {
    const formData = new FormData();
    formData.append('pattern', data.pattern);
    formData.append('tire_size', data.tire_size);
    formData.append('dot', data.dot);
    formData.append('tire_position', data.tire_position);
    formData.append('shop', data.shop);
    formData.append('code', data.code);
    formData.append('invoice_number', data.invoice_number);

    deletedTirePhotoIds?.forEach((photoId) => {
      formData.append('deleted_tire_photo_ids[]', photoId.toString());
    });

    newTirePhotos?.forEach((photo) => {
      formData.append('tire_photos[]', photo);
    });

    deletedReceiptPhotoIds?.forEach((photoId) => {
      formData.append('deleted_receipt_photo_ids[]', photoId.toString());
    });

    newReceiptPhotos?.forEach((photo) => {
      formData.append('receipt_photos[]', photo);
    });

    const response = await api.post<TireResponse>(`${API_BASE}/${tireId}?_method=PUT`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Get tire statistics for admin dashboard
   */
  async getStats(): Promise<TireStatsResponse> {
    const response = await api.get(`${API_BASE}/stats`);
    return response.data;
  },

  /**
   * Get per-brand dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStatsResponse> {
    const response = await api.get('/admin/dashboard-stats');
    return response.data;
  },

  /**
   * Delete a tire registration (admin only)
   */
  async deleteTire(tireId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${API_BASE}/${tireId}`);
    return response.data;
  },

  /**
   * Get all tires for export (no pagination)
   */
  async getTiresForExport(filters: TireFilters): Promise<Tire[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand.toString());

    const response = await api.get(`${API_BASE}/export-data?${params.toString()}`);
    return response.data.data || [];
  }
};

